import { axisLeft, ScaleBand, Selection, EnterElement, Axis } from "d3"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { ReactCallbacks } from "../../../../Types/ReactCallbacks"
import { MobergFontFamily } from "../../../../../../../../Moberg"
import { AXIS_TICK_FONT_SIZE } from "../../Constants"

export type D3BandAxisConfig = {
	scale: ScaleBand<string>
	tickValues?: string[]
	formatTick?: (value: string) => string
	label?: string
}

export class D3BandAxis extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3BandAxisConfig, ReactCallbacks<any>> {
	private axisClassName = "d3-band-axis"
	private labelClassName = "d3-band-axis-label"

	constructor(root: SVGGElement, config: D3BandAxisConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-band-axis-wrapper", reactCallbacks)
		this.render()
	}

	protected updateDerivedState(): void {
		// no derived children for now
	}

	protected enter(newElements: Selection<EnterElement, any, any, any>): Selection<SVGGElement, any, any, any> {
		const axisGroup = newElements.append("g").attr("class", this.className)
		const axisGenerator = this.getAxisGenerator()

		axisGroup
			.append<SVGGElement>("g")
			.attr("class", this.axisClassName)
			.call(axisGenerator as (selection: Selection<SVGGElement, any, any, any>) => void)
			.style("user-select", "none")

		this.styleAxis(axisGroup.select<SVGGElement>(`.${this.axisClassName}`))

		if (this.config.label) {
			const [rangeStart = 0, rangeEnd = 0] = this.config.scale.range()
			const center = (rangeStart + rangeEnd) / 2

			axisGroup
				.append<SVGTextElement>("text")
				.attr("class", this.labelClassName)
				.attr("text-anchor", "middle")
				.attr("transform", "rotate(-90)")
				.attr("x", -center)
				.attr("y", -50)
				.attr("fill", "var(--color-chart-axis-text)")
				.text(this.config.label)
		}

		return axisGroup
	}

	protected update(updateElements: Selection<any, any, any, any>): Selection<any, any, any, any> {
		const axisGenerator = this.getAxisGenerator()
		const [rangeStart = 0, rangeEnd = 0] = this.config.scale.range()
		const center = (rangeStart + rangeEnd) / 2

		updateElements
			.select<SVGGElement>(`.${this.axisClassName}`)
			.call(axisGenerator as (selection: Selection<SVGGElement, any, any, any>) => void)

		this.styleAxis(updateElements.select<SVGGElement>(`.${this.axisClassName}`))

		if (this.config.label) {
			updateElements
				.select<SVGTextElement>(`.${this.labelClassName}`)
				.attr("transform", "rotate(-90)")
				.attr("x", -center)
				.attr("y", -50)
				.attr("fill", "var(--color-chart-axis-text)")
				.text(this.config.label)
		}

		return updateElements
	}

	private getAxisGenerator(): Axis<string> {
		const tickValues = this.config.tickValues ?? this.config.scale.domain()
		const formatTick = this.config.formatTick ?? ((value: string) => value)
		return axisLeft(this.config.scale).tickValues(tickValues).tickFormat(value => formatTick(String(value))).tickSizeOuter(0)
	}

	private styleAxis(axis: Selection<SVGGElement, any, any, any>): void {
		axis
			.style("font-size", AXIS_TICK_FONT_SIZE)
			.style("font-family", MobergFontFamily.REGULAR)

		axis.selectAll(".domain, .tick line")
			.attr("stroke", "var(--color-chart-axis-text)")

		axis.selectAll(".tick text")
			.attr("fill", "var(--color-chart-axis-text)")
	}
}
